/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2023
 * @flow
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import { cyan } from '@material-ui/core/colors';
import {
  AttachMoney,
  LocalAtm,
  ListAlt,
  PeopleOutline,
} from '@material-ui/icons';
import Card from '../Card';
import Table from '../Table';
import { backgroundColors, borderColors } from '../../constants';
import PieChart from '../PieChart';
import { getHourIn12HrFormat } from '../../utils';
import HourlySalesChart from './HourlySalesChart';

const revenueCenterTableColumns = [
  {
    field: 'orderType',
    headerName: 'Order Type',
    flex: 1,
    sortable: true,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'totalCheck',
    headerName: 'Checks',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.totalCheck - nodeB.data.totalCheck;
    },
  },
  {
    field: 'formattedTotalAmount',
    headerName: 'Total',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.totalAmount - nodeB.data.totalAmount;
    },
  },
  {
    field: 'formattedAverageAmount',
    headerName: 'Average',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.averageAmount - nodeB.data.averageAmount;
    },
  },
];

const hourlySalesTableColumns = [
  {
    field: 'formattedTime',
    headerName: 'Time',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.time - nodeB.data.time;
    },
  },
  {
    field: 'totalCheck',
    headerName: 'Checks',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.totalCheck - nodeB.data.totalCheck;
    },
  },
  {
    field: 'formattedTotalAmount',
    headerName: 'Total',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.totalAmount - nodeB.data.totalAmount;
    },
  },
  {
    field: 'formattedAverageAmount',
    headerName: 'Average',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.averageAmount - nodeB.data.averageAmount;
    },
  },
];

const Checks = ({ commonData, revenueCentersData, orderTypesData, hourlySales }) => {
  const { currencyInfo } = useSelector(state => state);
  const { currency } = currencyInfo || {};
  const { totalCheck, totalAmount, totalGuest, } = commonData;

  //cards data
  const checkAverageAmount = (totalAmount && totalCheck) ? totalAmount / totalCheck : 0;
  const guestAverageAmount = (totalAmount && totalGuest) ? totalAmount / totalGuest : 0;
  const cards = [
    { label: 'Checks', value: totalCheck, icon: ListAlt },
    { label: 'Total', value: `${currency}${totalAmount.toFixed(2)}`, icon: AttachMoney },
    { label: 'Check Average', value: `${currency}${checkAverageAmount.toFixed(2)}`, icon: LocalAtm },
    { label: 'Guests', value: totalGuest, icon: PeopleOutline },
    { label: 'Guest Average', value: `${currency}${guestAverageAmount.toFixed(2)}`, icon: LocalAtm },
  ];

  //revenue centers pie data
  const revCentersData = Array.from(revenueCentersData.values());
  const rCentersPieData = {
    labels: revCentersData.map(it => it.orderTypeName),
    datasets: [
      {
        label: 'Revenue Centers',
        data: revCentersData.map(data => (data.totalCheck && totalCheck) ? ((data.totalCheck / totalCheck) * 100).toFixed(2) : 0),
        backgroundColor: revCentersData.map((_, index) => backgroundColors[index % backgroundColors.length]),
        borderColor: revCentersData.map((_, index) => borderColors[index % borderColors.length]),
        borderWidth: 1,
      },
    ],
  };
  const rCenterTableRows = revCentersData.map(it => ({
    id: it.id,
    orderType: it.orderTypeName,
    totalCheck: it.totalCheck,
    totalAmount: it.totalAmount,
    formattedTotalAmount: `${currency}${it.totalAmount.toFixed(2)}`,
    averageAmount: it.averageAmount,
    formattedAverageAmount: `${currency}${it.averageAmount.toFixed(2)}`,
  }));

  //order types pie data
  const odrTypesData = Array.from(orderTypesData.values());
  const orderTypesPieData = {
    labels: odrTypesData.map(it => it.orderTypeName),
    datasets: [
      {
        label: 'Order Types',
        data: odrTypesData.map(data => (data.totalCheck && totalCheck) ? ((data.totalCheck / totalCheck) * 100).toFixed(2) : 0),
        backgroundColor: odrTypesData.map((_, index) => backgroundColors[index % backgroundColors.length]),
        borderColor: odrTypesData.map((_, index) => borderColors[index % borderColors.length]),
        borderWidth: 1,
      },
    ],
  };
  const orderTypesTableRows = odrTypesData.map(it => ({
    id: it.id,
    orderType: it.orderTypeName,
    totalCheck: it.totalCheck,
    totalAmount: it.totalAmount,
    formattedTotalAmount: `${currency}${it.totalAmount.toFixed(2)}`,
    averageAmount: it.averageAmount,
    formattedAverageAmount: `${currency}${it.averageAmount.toFixed(2)}`,
  }));

  //hourly sales
  const timeSlots = Array.from(hourlySales.keys()).map(hours => getHourIn12HrFormat(hours));
  const hourlySalesData = Array.from(hourlySales.values());
  const hourlySalesChartData = {
    labels: timeSlots,
    datasets: [
      {
        type: 'bar',
        label: 'Total Checks',
        data: hourlySalesData.map(it => it.totalCheck),
        backgroundColor: 'rgba(53, 162, 235, 1)',
      },
      {
        type: 'line',
        label: 'Total Amount',
        data: hourlySalesData.map(it => it.totalAmount),
        borderColor: 'rgb(255, 99, 132, 1)',
        borderWidth: 1,
        currency: currency,
      },
      {
        type: 'line',
        label: 'Average Amount',
        data: hourlySalesData.map(it => it.averageAmount),
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
        currency: currency,
      },
    ],
  };
  const hourlySalesTableRows = hourlySalesData.map(it => ({
    id: it.id,
    time: it.hour,
    formattedTime: getHourIn12HrFormat(it.hour),
    totalCheck: it.totalCheck,
    totalAmount: it.totalAmount,
    formattedTotalAmount: `${currency}${it.totalAmount.toFixed(2)}`,
    averageAmount: it.averageAmount,
    formattedAverageAmount: `${currency}${it.averageAmount.toFixed(2)}`,
  }));

  return (
    <React.Fragment>
      <Grid container>
        {cards.map((card, i) =>
          <Grid item xs={12} sm={6} md={4} lg={3} key={`key - ${card.label}`}>
            <Card
              label={card.label}
              value={card.value}
              icon={card.icon}
              backgroundColor={cyan[500]}
            />
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={6} lg={6}>
          <PieChart
            data={rCentersPieData}
            title={'REVENUE CENTERS'}
            formatter={(value, context) => `${Math.trunc(value)}%`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={6}>
          <Table
            rowData={rCenterTableRows}
            columnDefs={revenueCenterTableColumns}
            title={'REVENUE CENTERS'}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={6} lg={6}>
          <PieChart
            data={orderTypesPieData}
            title={'ORDER TYPES'}
            formatter={(value, context) => `${Math.trunc(value)}%`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={6}>
          <Table
            rowData={orderTypesTableRows}
            columnDefs={revenueCenterTableColumns}
            title={'ORDER TYPES'}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={6} lg={6}>
          <HourlySalesChart data={hourlySalesChartData} title={'HOURLY SALES'} />
        </Grid>
        <Grid item xs={12} sm={6} lg={6}>
          <Table
            rowData={hourlySalesTableRows}
            columnDefs={hourlySalesTableColumns}
            title={'HOURLY SALES'}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Checks;
